import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    Stack,
    Typography,
} from '@mui/material';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const loadImage = (file) => new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
    };
    image.onerror = (error) => {
        URL.revokeObjectURL(objectUrl);
        reject(error);
    };
    image.src = objectUrl;
});

const canvasToBlob = (canvas, type = 'image/png', quality = 0.95) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (!blob) {
            reject(new Error('Не удалось обработать изображение'));
            return;
        }
        resolve(blob);
    }, type, quality);
});

const buildCroppedFile = async (file, options) => {
    const {
        aspectRatio,
        outputWidth,
        outputHeight,
        zoom,
        offsetX,
        offsetY,
    } = options;

    const image = await loadImage(file);
    const imageAspect = image.width / image.height;

    let baseWidth;
    let baseHeight;
    if (imageAspect > aspectRatio) {
        baseHeight = image.height;
        baseWidth = baseHeight * aspectRatio;
    } else {
        baseWidth = image.width;
        baseHeight = baseWidth / aspectRatio;
    }

    const zoomedWidth = baseWidth / zoom;
    const zoomedHeight = baseHeight / zoom;

    const maxOffsetX = (baseWidth - zoomedWidth) / 2;
    const maxOffsetY = (baseHeight - zoomedHeight) / 2;

    const centerX = (image.width / 2) + (offsetX * maxOffsetX);
    const centerY = (image.height / 2) + (offsetY * maxOffsetY);

    const sx = clamp(centerX - (zoomedWidth / 2), 0, image.width - zoomedWidth);
    const sy = clamp(centerY - (zoomedHeight / 2), 0, image.height - zoomedHeight);

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Не удалось получить контекст canvas');
    }

    context.drawImage(
        image,
        sx,
        sy,
        zoomedWidth,
        zoomedHeight,
        0,
        0,
        outputWidth,
        outputHeight
    );

    const blob = await canvasToBlob(canvas, 'image/png');
    const originalName = file.name.replace(/\.[^/.]+$/, '');
    return new File([blob], `${originalName}_cropped.png`, { type: 'image/png' });
};

export const ImageCropDialog = ({
    open,
    file,
    title,
    aspectRatio,
    outputWidth,
    outputHeight,
    helperText,
    onCancel,
    onApply,
    // Скругление рамки предпросмотра (например '15.9%' — как у карточек в приложении).
    // На сам файл не влияет: PNG сохраняется прямоугольным, скругляет уже приложение.
    previewBorderRadius,
    // Максимальная ширина области предпросмотра (для маленьких квадратных карточек удобнее меньше)
    previewMaxWidth = 780,
}) => {
    const [zoom, setZoom] = useState(1.1);
    const [offsetX, setOffsetX] = useState(0);
    const [offsetY, setOffsetY] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const previewUrl = useMemo(() => {
        if (!file) return null;
        return URL.createObjectURL(file);
    }, [file]);

    useEffect(() => () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
    }, [previewUrl]);

    useEffect(() => {
        if (open) {
            setZoom(1.1);
            setOffsetX(0);
            setOffsetY(0);
            setSubmitting(false);
        }
    }, [open]);

    const handleApply = async () => {
        if (!file) return;
        setSubmitting(true);
        try {
            const croppedFile = await buildCroppedFile(file, {
                aspectRatio,
                outputWidth,
                outputHeight,
                zoom,
                offsetX,
                offsetY,
            });
            onApply(croppedFile);
        } catch (error) {
            onApply(null, error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {!!helperText && (
                        <Typography variant="body2" color="text.secondary">
                            {helperText}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            maxWidth: previewMaxWidth,
                            mx: 'auto',
                            aspectRatio: `${aspectRatio}`,
                            borderRadius: previewBorderRadius ?? 2,
                            overflow: 'hidden',
                            backgroundColor: '#111',
                        }}
                    >
                        {previewUrl && (
                            <img
                                src={previewUrl}
                                alt="crop-preview"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transform: `translate(${offsetX * 28}%, ${offsetY * 28}%) scale(${zoom})`,
                                    transformOrigin: 'center center',
                                }}
                            />
                        )}
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary">Масштаб</Typography>
                        <Slider
                            min={1}
                            max={2.5}
                            step={0.01}
                            value={zoom}
                            onChange={(_, value) => setZoom(value)}
                            disabled={submitting}
                        />
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary">Смещение по горизонтали</Typography>
                        <Slider
                            min={-1}
                            max={1}
                            step={0.01}
                            value={offsetX}
                            onChange={(_, value) => setOffsetX(value)}
                            disabled={submitting}
                        />
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary">Смещение по вертикали</Typography>
                        <Slider
                            min={-1}
                            max={1}
                            step={0.01}
                            value={offsetY}
                            onChange={(_, value) => setOffsetY(value)}
                            disabled={submitting}
                        />
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={submitting}>Отмена</Button>
                <Button onClick={handleApply} variant="contained" disabled={!file || submitting}>
                    {submitting ? 'Обрезаем...' : 'Применить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
